// =============================================================================
// Unified platform detection — single source of truth.
//
// Every file that needs to know "what kind of client is this?" must import
// from here.  Ad-hoc `window.webkit?.messageHandlers` / `navigator.userAgent`
// checks elsewhere are prohibited.
// =============================================================================

export interface PlatformInfo {
  /** Maestro iOS app (WKWebView shell with __maestroNative injected). */
  isIOSNativeApp: boolean;
  /** iPhone/iPad running in Mobile Safari or Chrome (no native bridge). */
  isIOSMobileSafari: boolean;
  /** iOS home-screen PWA (standalone mode). */
  isIOSStandalone: boolean;
  /** Android device (native shell). */
  isAndroid: boolean;
  /** Tauri desktop app (macOS / Windows / Linux). */
  isTauriDesktop: boolean;
  /** Plain browser on a desktop/laptop (no Tauri). */
  isWebDesktop: boolean;

  /** True for any iPhone/iPad (native, Safari, or PWA). */
  isAnyIOS: boolean;
  /** True when a native bridge exists that can route UPnP/transport commands. */
  hasNativeBridge: boolean;
  /** True when the device runs local LAN discovery (Tauri or native app). */
  hasNativeDiscovery: boolean;

  /** Human-readable device label for BATON registration.  */
  deviceName: string;
  /** Short lowercase token for WebSocket URL / session prefix. */
  deviceType: string;
  /** Prefix for the persistent device ID stored in localStorage. */
  deviceIdPrefix: string;
}

let _cached: PlatformInfo | null = null;

export function detectPlatform(): PlatformInfo {
  if (_cached) return _cached;

  if (typeof window === 'undefined') {
    _cached = {
      isIOSNativeApp: false,
      isIOSMobileSafari: false,
      isIOSStandalone: false,
      isAndroid: false,
      isTauriDesktop: false,
      isWebDesktop: true,
      isAnyIOS: false,
      hasNativeBridge: false,
      hasNativeDiscovery: false,
      deviceName: 'Server',
      deviceType: 'unknown',
      deviceIdPrefix: 'srv',
    };
    return _cached;
  }

  const w = window as any;
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const uaLower = ua.toLowerCase();

  const hasIOSBridge = !!(w.__maestroNative?.platform === 'ios');
  const hasAndroidBridge = !!(w.__maestroNative?.platform === 'android');
  const hasTauri = !!w.__TAURI__;
  const isIPhoneUA = uaLower.includes('iphone');
  const isIPadUA = uaLower.includes('ipad');
  const isIOSUA = isIPhoneUA || isIPadUA;
  const isStandalone = typeof navigator !== 'undefined' && !!(navigator as any).standalone;

  const isIOSNativeApp = hasIOSBridge;
  const isIOSStandalone = isIOSUA && !isIOSNativeApp && isStandalone;
  const isIOSMobileSafari = isIOSUA && !isIOSNativeApp && !isStandalone;
  const isAndroid = hasAndroidBridge;
  const isTauriDesktop = hasTauri && !hasIOSBridge && !hasAndroidBridge;
  const isAnyIOS = isIOSNativeApp || isIOSMobileSafari || isIOSStandalone;
  const isWebDesktop = !isAnyIOS && !isAndroid && !isTauriDesktop;

  const hasNativeBridge = isIOSNativeApp || isTauriDesktop;
  const hasNativeDiscovery = isIOSNativeApp || isTauriDesktop || isAndroid;

  let deviceName = 'Browser';
  if (isIOSNativeApp) {
    deviceName = isIPadUA ? 'iPad' : 'iPhone';
  } else if (isIOSMobileSafari) {
    deviceName = isIPadUA ? 'iPad (Safari)' : 'iPhone (Safari)';
  } else if (isIOSStandalone) {
    deviceName = isIPadUA ? 'iPad' : 'iPhone';
  } else if (isTauriDesktop) {
    deviceName = 'Desktop';
  } else if (!!w.__MAESTRO_CONNECTOR__) {
    deviceName = 'Desktop';
  } else if (/Mac OS X|Macintosh/i.test(ua)) {
    deviceName = 'Mac';
  }

  let deviceType = 'browser';
  if (isIOSNativeApp) deviceType = isIPadUA ? 'ipad' : 'iphone';
  else if (isIOSMobileSafari || isIOSStandalone) deviceType = isIPadUA ? 'ipad-safari' : 'iphone-safari';
  else if (isAndroid) deviceType = 'android';
  else if (isTauriDesktop) deviceType = 'desktop-connector';
  else if (uaLower.includes('mobile')) deviceType = 'mobile';

  let deviceIdPrefix = 'web';
  if (isIOSNativeApp) deviceIdPrefix = 'ios';
  else if (isIOSMobileSafari || isIOSStandalone) deviceIdPrefix = 'iosweb';
  else if (isAndroid) deviceIdPrefix = 'and';
  else if (isTauriDesktop) deviceIdPrefix = 'desk';

  _cached = {
    isIOSNativeApp,
    isIOSMobileSafari,
    isIOSStandalone,
    isAndroid,
    isTauriDesktop,
    isWebDesktop,
    isAnyIOS,
    hasNativeBridge,
    hasNativeDiscovery,
    deviceName,
    deviceType,
    deviceIdPrefix,
  };

  return _cached;
}

/**
 * Force re-evaluation (e.g. after the native bridge injects late).
 * Used by AppLayout once at startup after waiting for bridge readiness.
 */
export function resetPlatformCache(): void {
  _cached = null;
}

// =============================================================================
// Legacy API — delegates to detectPlatform() for backward compatibility.
// Existing imports continue to work without changing call sites in hooks/pages.
// =============================================================================

export type MaestroPlatform = 'tauri' | 'ios' | 'android' | 'web';

export function getPlatform(): MaestroPlatform {
  const p = detectPlatform();
  if (p.isIOSNativeApp) return 'ios';
  if (p.isAndroid) return 'android';
  if (p.isTauriDesktop) return 'tauri';
  return 'web';
}

export function isNativeApp(): boolean {
  const p = detectPlatform();
  return p.isIOSNativeApp || p.isTauriDesktop || p.isAndroid;
}

export function isIOSNativeApp(): boolean {
  return detectPlatform().isIOSNativeApp;
}

/** Maestro iOS WKWebView shell — App Store login-only gate (#408). */
export function isIosShell(): boolean {
  return detectPlatform().isIOSNativeApp;
}

export function isTauriApp(): boolean {
  return detectPlatform().isTauriDesktop;
}

export function hasNativeDiscovery(): boolean {
  return detectPlatform().hasNativeDiscovery;
}

export function isDesktopMacOSUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/(iPhone|iPad|iPod)/i.test(ua)) return false;
  return /Mac OS X|Macintosh/i.test(ua);
}

/** Cmd+F on macOS, Ctrl+F elsewhere — Maestro search (not native in-page find). */
export function isFindShortcut(
  e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
): boolean {
  if (e.key !== 'f' && e.key !== 'F') return false;
  if (e.altKey || e.shiftKey) return false;
  return isDesktopMacOSUserAgent() ? e.metaKey : e.ctrlKey;
}

/** Tauri desktop OS from Connector init (`std::env::consts::OS` via `get_connector_info`). */
export type TauriDesktopOs = 'macos' | 'windows' | 'linux';

/** Normalize Rust/Tauri/UA platform tokens to a canonical desktop OS id. */
export function normalizeDesktopOsToken(raw: unknown): TauriDesktopOs | null {
  const p = String(raw ?? '').trim().toLowerCase();
  if (p === 'macos' || p === 'darwin' || p === 'mac') return 'macos';
  if (p === 'windows' || p === 'win32' || p === 'win') return 'windows';
  if (p === 'linux') return 'linux';
  return null;
}

/**
 * True when running inside the Windows Connector (WebView2 + Tauri).
 * Sync-safe: uses cached IPC bootstrap when available, else UA/platform heuristics.
 */
export function isWindowsConnectorHost(): boolean {
  if (typeof window === 'undefined') return false;

  if (import.meta.env.DEV) {
    try {
      const dev = normalizeDesktopOsToken(localStorage.getItem('maestro_dev_os'));
      if (dev === 'windows') return true;
      if (dev === 'macos' || dev === 'linux') return false;
    } catch { /* ignore */ }
  }

  const cached = normalizeDesktopOsToken((window as any).__MAESTRO_DESKTOP_OS__);
  if (cached === 'windows') return true;
  if (cached === 'macos' || cached === 'linux') return false;

  const fromGlobal = normalizeDesktopOsToken((window as any)?.__MAESTRO_CONNECTOR__?.platform);
  if (fromGlobal === 'windows') return true;
  if (fromGlobal === 'macos' || fromGlobal === 'linux') return false;

  const hasTauri = !!(window as any).__TAURI__;
  if (!hasTauri) return false;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/Windows/i.test(ua)) return true;
  const plat = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
  if (/Win/i.test(plat)) return true;

  return false;
}

/**
 * Returns the Connector host OS. Dev-only override: localStorage `maestro_dev_os`
 * set to macos | windows | linux (for modal copy QA in the browser shell).
 */
export function getTauriDesktopOs(): TauriDesktopOs {
  if (typeof window === 'undefined') return 'macos';

  if (import.meta.env.DEV) {
    try {
      const dev = normalizeDesktopOsToken(localStorage.getItem('maestro_dev_os'));
      if (dev) return dev;
    } catch { /* ignore */ }
  }

  const cached =
    normalizeDesktopOsToken((window as any).__MAESTRO_DESKTOP_OS__)
    ?? normalizeDesktopOsToken((window as any)?.__MAESTRO_CONNECTOR__?.platform);
  if (cached) return cached;

  if (isWindowsConnectorHost()) return 'windows';

  if ((window as any).__TAURI__) {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    if (/Mac OS X|Macintosh/i.test(ua) && !/Windows/i.test(ua)) return 'macos';
    const plat = typeof navigator !== 'undefined' ? navigator.platform || '' : '';
    if (/Linux/i.test(plat)) return 'linux';
  }

  if (typeof navigator !== 'undefined') {
    const p = navigator.platform || '';
    if (/Win/i.test(p)) return 'windows';
    if (/Linux/i.test(p)) return 'linux';
    if (/Mac|iPhone|iPad/i.test(p)) return 'macos';
  }

  return 'macos';
}
